import { Box } from "@chakra-ui/react";

import NewTable from "components/NewTable";
import useListarContasParaExtrato from "core/features/conciliacao-bancaria/hooks/useListaContasParaExtrato";
import useContaTableColumns from "./../hooks/useContaTableColumns";
import { ContaParaExtratoBancarioResponse } from "features/conciliacao-bancaria/typings";
import { useState, useEffect } from "react";
import HeaderTable from "components/Page/HeaderTable";
import { useMemo } from "react";
import { OperacaoEnum } from "core/resources/enums/conta";
import moment from "moment";
import { Flex } from "@chakra-ui/layout";
import SelectableButton from "components/Button/SelectableButton";
import { useContaStore } from "features/conta/store";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { MdCheck, MdPreview } from "react-icons/md";
import { ContaItemResponse } from "core/features/conta/typings";
import useContas from "core/features/conta/hooks/useContas";
import { showError } from "infra/helpers/alerta";
import { useLoading } from "components/GlobalLoading";
import DateRangePicker from "components/Input/DateRangePicker";

interface ModalConciliarProps {
  setValue: any;
  setSelecteds: any;
  defaultSelectedRows?: ContaParaExtratoBancarioResponse[];
  selectRowVisible?: boolean;
  extratoId: string;
  extratoData: string;
  operacao: OperacaoEnum;
  conciliado?: boolean | undefined;
  contasBancariasIds?: string[];
  setHistoryId?: (id: string) => void;
  setPagarContaId?: (id: string) => void;
  setLiquidarContasData?: (data: ContaItemResponse[]) => void;
  minH?: string;
  maxH?: string;
}

export default function ListaContas({
  setValue,
  setSelecteds,
  defaultSelectedRows = [],
  selectRowVisible,
  extratoId,
  extratoData,
  conciliado,
  operacao,
  contasBancariasIds,
  setHistoryId,
  setPagarContaId,
  setLiquidarContasData,
  minH = "45vh",
  maxH = "60vh",
}: ModalConciliarProps) {
  const { filters, update } = useContaStore();
  const [search, setSearch] = useState<string | undefined>(undefined);

  const columns = useContaTableColumns(operacao, filters.tipoData);

  const [selectedRows, setSelectedRows] =
    useState<ContaParaExtratoBancarioResponse[]>(defaultSelectedRows);

  const tipoDataNaoLiquidado = filters.tipoData === "vencimento";

  const {
    isLoading: isLoadingContas,
    isFetching: isFetchingContas,
    data,
  } = useListarContasParaExtrato({
    dataInicio: tipoDataNaoLiquidado
      ? filters.dataInicio
      : moment(extratoData)
          .subtract(conciliado ? 1 : 0, "month")
          .startOf("day")
          .format("YYYY-MM-DD"),
    dataFim: tipoDataNaoLiquidado
      ? filters.dataFim
      : moment(extratoData)
          .add(conciliado ? 1 : 0, "month")
          .endOf("day")
          .format("YYYY-MM-DD"),
    extratosBancariosIds: conciliado ? [extratoId] : undefined,
    operacao,
    search,
    naoConciliado: conciliado ? undefined : "1",
    contasBancariasIds,
    tipoData: filters.tipoData,
  });

  const {
    isLoading: isLoadingContasLiquidar,
    isFetching: isFetchingContasLiquidar,
    refetch: refetchContasLiquidar,
  } = useContas({
    contasIds: selectedRows.map((s) => s.id),
    limit: 1000,
    enabled: false,
    operacao,
  });

  useEffect(() => {
    setValue("transferenciaId", undefined);
    setSelecteds(selectedRows);
  }, [selectedRows, setSelecteds, setValue]);

  const items = useMemo(() => {
    return data?.items || [];
  }, [data]);

  const onLiquidarMultiplasContas = async () => {
    refetchContasLiquidar().then(({ data: contasLiquidar }) => {
      if (contasLiquidar) {
        setLiquidarContasData?.(contasLiquidar.items);
      } else {
        showError(
          "Ocorreu um erro ao buscar as contas para liquidar. Tente novamente."
        );
      }
    });
  };

  useLoading(
    isLoadingContasLiquidar || isFetchingContasLiquidar,
    "Buscando dados para liquidação..."
  );

  return (
    <>
      <Box
        mt="8px"
        pt="8px"
        px="8px"
        id="lista-contas-conciliacao"
        className="custom-scroll-bar"
        minH={minH}
        maxH={maxH}
        overflowY="auto"
      >
        <NewTable
          defaultSelectedRows={defaultSelectedRows}
          key={filters.tipoData}
          loading={isLoadingContas || isFetchingContas}
          dataSource={items ?? []}
          columns={columns}
          showCustomSelectionActions={tipoDataNaoLiquidado}
          selectedRowsKeyExtractor={(v) => v.map((s) => s.id)}
          customSelectedRows={
            selectRowVisible
              ? {
                  handler: setSelectedRows,
                  selecteds: selectedRows,
                  actions: tipoDataNaoLiquidado
                    ? [
                        {
                          title: "Liquidar",
                          onClick: () => {
                            onLiquidarMultiplasContas();
                          },
                        },
                      ]
                    : [],
                }
              : undefined
          }
          title={() => (
            <HeaderTable
              value={search ?? ""}
              onSearch={(q?: string) => setSearch(q)}
              childrenFlex={0.35}
              children={
                <Flex gap={4} alignItems="center" width="100%">
                  <SelectableButton
                    selected={filters.tipoData === "movimentacao"}
                    onClick={() => {
                      setSelectedRows([]);
                      update({
                        tipoData: "movimentacao",
                      });
                    }}
                    rounded="full"
                    fontSize="14px"
                  >
                    Liquidado
                  </SelectableButton>

                  <SelectableButton
                    selected={filters.tipoData === "vencimento"}
                    onClick={() => {
                      setSelectedRows([]);
                      update({
                        tipoData: "vencimento",
                      });
                    }}
                    rounded="full"
                    fontSize="14px"
                  >
                    Não liquidado
                  </SelectableButton>

                  {tipoDataNaoLiquidado ? (
                    <Box w="250px">
                      <DateRangePicker
                        allowEmptyDates={{
                          dataInicio: false,
                          dataFim: false,
                        }}
                        onChange={(dataInicio, dataFim) => {
                          update({
                            dataInicio: dataInicio,
                            dataFim: dataFim,
                          });
                        }}
                        value={{
                          dataInicio: filters.dataInicio!,
                          dataFim: filters.dataFim!,
                        }}
                      />
                    </Box>
                  ) : null}
                </Flex>
              }
            />
          )}
          actions={[
            {
              title: "Liquidar",
              icon: () => <MdCheck />,
              onClick: (v) => {
                setPagarContaId?.(v.id);
              },
              shouldRender: (v) => !v.dataPagamento,
            },
            {
              title: "Ver histórico",
              icon: () => <MdPreview />,
              permission:
                operacao === OperacaoEnum.Despesa
                  ? PermissaoEnum.Backoffice.Financeiro.ContasPagar.Visualizar
                  : PermissaoEnum.Backoffice.Financeiro.ContasReceber
                      .Visualizar,
              onClick: (v) => {
                setHistoryId?.(v.id);
              },
            },
          ]}
        />
      </Box>
    </>
  );
}
